import type { Canvas, Object as FabricObject } from 'fabric';
import type { SetStateAction } from 'jotai';
import type { RefObject } from 'react';
import { useCallback } from 'react';
import type { SelectedObjectState } from '../store/atoms/editorSelectionAtom';
import type { CanvasVariant, SlideData, VariantKey } from '../store/atoms/slideAtom';
import { getBannerCtaGroup, isBannerCtaOrChild } from './useObjectTransform';

const THUMB_W = 104;
const THUMB_H = 64;

export interface UseCanvasSyncParams {
  readonly desktopCanvasRef: RefObject<Canvas | null>;
  readonly mobileCanvasRef: RefObject<Canvas | null>;
  readonly setSlide: (update: SetStateAction<SlideData>) => void;
  readonly setSelection: (value: SetStateAction<SelectedObjectState | null>) => void;
}

/**
 * Sincroniza o estado do canvas com o átomo do slide e o átomo de seleção.
 *
 * @param params - Refs dos canvases e setters dos átomos
 * @returns `saveCurrentSlide`, `generateThumbnail`, `syncSelectionAtom`
 */
export function useCanvasSync({
  desktopCanvasRef,
  mobileCanvasRef,
  setSlide,
  setSelection,
}: UseCanvasSyncParams) {
  const generateThumbnail = useCallback((canvas: Canvas): string | undefined => {
    const el = canvas.getElement();
    const offscreen = document.createElement('canvas');
    offscreen.width = THUMB_W;
    offscreen.height = THUMB_H;
    const ctx2d = offscreen.getContext('2d');
    if (!ctx2d) return undefined;
    ctx2d.drawImage(el, 0, 0, el.width, el.height, 0, 0, THUMB_W, THUMB_H);
    return offscreen.toDataURL('image/jpeg', 0.75);
  }, []);

  const saveCurrentSlide = useCallback(
    (key: VariantKey) => {
      const device = key.endsWith('desktop') ? 'desktop' : 'mobile';
      const canvas = device === 'desktop' ? desktopCanvasRef.current : mobileCanvasRef.current;
      if (!canvas) return;
      const json = canvas.toObject(['data']) as Record<string, unknown>;
      const bg = canvas.backgroundColor as string;
      const thumbnail = generateThumbnail(canvas);
      setSlide(prev => {
        // Preserva fundos de imagem/vídeo da variante: eles vivem em campos
        // próprios e seriam descartados ao reconstruir a variante a partir do canvas.
        const prevVariant = prev.variants[key];
        const updatedVariant: CanvasVariant = {
          canvas: json,
          backgroundColor: bg,
          ...(prevVariant?.backgroundImage ? { backgroundImage: prevVariant.backgroundImage } : {}),
          ...(prevVariant?.backgroundVideo ? { backgroundVideo: prevVariant.backgroundVideo } : {}),
          ...(thumbnail ? { thumbnail } : {}),
        };
        let updatedVariants = { ...prev.variants, [key]: updatedVariant };
        // Propaga edições de pt-BR para as demais variantes do mesmo device
        if (key === `pt-BR-${device}`) {
          const copy = (targetKey: VariantKey): CanvasVariant => {
            const targetVariant = prev.variants[targetKey];
            return {
              canvas: JSON.parse(JSON.stringify(json)) as Record<string, unknown>,
              backgroundColor: bg,
              ...(targetVariant?.backgroundImage
                ? { backgroundImage: targetVariant.backgroundImage }
                : {}),
              ...(targetVariant?.backgroundVideo
                ? { backgroundVideo: targetVariant.backgroundVideo }
                : {}),
              ...(thumbnail ? { thumbnail } : {}),
            };
          };
          updatedVariants = {
            ...updatedVariants,
            [`en-US-${device}`]: copy(`en-US-${device}`),
            [`es-ES-${device}`]: copy(`es-ES-${device}`),
          };
        }
        return { ...prev, variants: updatedVariants };
      });
    },
    [desktopCanvasRef, mobileCanvasRef, generateThumbnail, setSlide],
  );

  const syncSelectionAtom = useCallback(
    (canvas: Canvas) => {
      const obj = canvas.getActiveObject();
      if (!obj || obj.type === 'activeSelection') {
        setSelection(null);
        return;
      }
      const isText = ['textbox', 'i-text', 'text'].includes(obj.type ?? '');
      interface TextLike {
        text: string;
        fontSize: number;
        fontFamily: string;
        fontWeight: string | number;
        fontStyle: string;
        lineHeight: number;
        charSpacing: number;
      }
      interface GroupLike {
        getObjects: () => FabricObject[];
        data?: { label?: string; href?: string; [key: string]: unknown };
      }
      const textObj = isText ? (obj as unknown as TextLike) : null;
      const objFill = obj.fill;

      // Detecta Banner CTA tanto quando o grupo pai está selecionado quanto
      // quando um filho está ativo (interactive: true seleciona o filho).
      const isBannerCta = isBannerCtaOrChild(obj);
      let href: string | undefined;
      if (isBannerCta) {
        const bannerGroup = getBannerCtaGroup(obj);
        if (bannerGroup) {
          const btnGroup = bannerGroup
            .getObjects()
            .find(
              o => o.type === 'group' && (o as unknown as GroupLike).data?.label === 'Saiba mais',
            );
          href = btnGroup ? String((btnGroup as unknown as GroupLike).data?.href ?? '') : '';
        }
      }

      setSelection({
        objectId: (obj as { data?: { id?: string } }).data?.id ?? '',
        type: obj.type ?? 'unknown',
        x: Math.round(obj.left ?? 0),
        y: Math.round(obj.top ?? 0),
        width: Math.round((obj.width ?? 0) * (obj.scaleX ?? 1)),
        height: Math.round((obj.height ?? 0) * (obj.scaleY ?? 1)),
        angle: Math.round(obj.angle ?? 0),
        opacity: Math.round((obj.opacity ?? 1) * 100),
        fill: typeof objFill === 'string' ? objFill : '#ffffff',
        stroke: typeof obj.stroke === 'string' ? obj.stroke : '#000000',
        strokeWidth: obj.strokeWidth ?? 0,
        text: textObj?.text ?? '',
        fontSize: textObj?.fontSize ?? 16,
        fontFamily: textObj?.fontFamily ?? 'Inter, sans-serif',
        fontWeight: textObj != null ? String(textObj.fontWeight ?? '400') : '400',
        fontStyle: textObj?.fontStyle ?? 'normal',
        lineHeight: textObj?.lineHeight ?? 1.4,
        charSpacing: textObj?.charSpacing ?? 0,
        ...(href !== undefined && { href }),
      });
    },
    [setSelection],
  );

  return { saveCurrentSlide, generateThumbnail, syncSelectionAtom } as const;
}
